"use client";

import { forwardRef } from "react";
import { cn } from "~/lib/utils";

export interface RadioOption {
  value: string;
  label: string;
}

export interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  name: string;
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
}

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, options, value, onChange, name, disabled = false, orientation = "horizontal", ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex gap-2",
          orientation === "vertical" ? "flex-col" : "flex-row items-center gap-6",
          className
        )}
        role="radiogroup"
        {...props}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex items-center gap-2 cursor-pointer",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => !disabled && onChange(e.target.value)}
              disabled={disabled}
              className="sr-only"
            />
            <div
              className={cn(
                "flex h-4 w-4 items-center justify-center rounded-full border",
                "transition-colors",
                value === option.value
                  ? "border-[#1c3144] bg-[#1c3144]"
                  : "border-[rgba(0,6,46,0.2)] bg-white"
              )}
            >
              {value === option.value && (
                <div className="h-1.5 w-1.5 rounded-full bg-white" />
              )}
            </div>
            <span className="text-sm leading-5 text-[#1c2024]">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }
);

RadioGroup.displayName = "RadioGroup";

export { RadioGroup };

